import { splitGros } from "@/lib/currency";

// Coin colors: gold / silver / copper / a dull, tarnished iron for groš
// (the smallest, least precious coin).
const COINS = [
  { key: "zlatka", label: "Zl", gradient: "radial-gradient(circle at 35% 30%, #FFE9A8, #C9971F)" },
  { key: "stribrny", label: "St", gradient: "radial-gradient(circle at 35% 30%, #F1F3F5, #9AA0A6)" },
  { key: "medak", label: "Mď", gradient: "radial-gradient(circle at 35% 30%, #E8A268, #9C5A2E)" },
  { key: "gros", label: "Gr", gradient: "radial-gradient(circle at 35% 30%, #8B8378, #4A443C)" },
];

const SIZES = {
  sm: { coin: 9, text: "text-xs", gap: "gap-1.5" },
  md: { coin: 12, text: "text-sm", gap: "gap-2.5" },
  lg: { coin: 16, text: "text-base", gap: "gap-3.5" },
};

// Visual replacement for plain formatGros() text - a row of coin badges
// (colored circles) instead of "1 Zl 2 St 7 Mď 9 Gr" as bare text, used
// wherever a money amount should read as coins, not a number. showFraction
// appends the leftover sub-groš remainder (e.g. unpaid dividend carry) next
// to the groš coin, since that's the one place a fraction below the
// smallest physical coin is meaningful and would otherwise silently vanish.
//
// Leading zero denominations are dropped - "3 Mď 5 Gr" rather than
// "0 Zl 0 St 3 Mď 5 Gr" - since a coin that isn't there yet doesn't help
// readability. Once the first non-zero denomination shows up, everything
// below it down to groš still renders (even a zero in between), so "1 St 0
// Mď 3 Gr" stays fully precise - only the leading run of zeros is trimmed.
export const CoinAmount = ({ amount, size = "md", showFraction = false, className = "", ...rest }) => {
  const split = splitGros(amount);
  const { coin, text, gap } = SIZES[size] ?? SIZES.md;
  const fraction = Number(split.fraction);

  const firstNonZero = COINS.findIndex(({ key }) => split[key] > 0);
  const visibleCoins = firstNonZero === -1 ? COINS.slice(-1) : COINS.slice(firstNonZero);

  return (
    <div className={`flex flex-wrap items-center ${gap} ${className}`} {...rest}>
      {visibleCoins.map(({ key, label, gradient }) => (
        <span key={key} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block shrink-0 rounded-full"
            style={{
              width: coin,
              height: coin,
              backgroundImage: gradient,
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.35), 0 1px 1px rgba(0,0,0,0.3)",
            }}
            aria-hidden="true"
          />
          <span className={`font-display font-600 text-white/90 ${text}`}>
            {split[key]} <span className="text-white/40">{label}</span>
          </span>
          {key === "gros" && showFraction && fraction > 0 && (
            <span className={`text-white/40 ${text}`}>(+{fraction.toFixed(2)})</span>
          )}
        </span>
      ))}
    </div>
  );
};
